import React, { useEffect, useState } from 'react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { Menu, X } from 'lucide-react';

import Button from '../../../components/Buttons/Button';
import Logotype from '../../../components/Logotype';
import useMediaQuery from '../../../hooks/MediaQuery';
import cn from '../../../lib/classNames';
import SocialMedia from '../SocialMedia/SocialMedia';

import styles from './MenuNav.module.scss';

type MenuNavType = {
	menuItems: { id: string; label: string; ref: React.RefObject<HTMLDivElement> }[];
	classPageContent?: string;
	showDemoButton?: boolean;
	isLoading?: boolean;
};

export default function MenuNav(props: MenuNavType) {
	const { menuItems, classPageContent, showDemoButton = true, isLoading = false } = props;
	const isMobile = useMediaQuery('(max-width: 767px)');
	const isTablet = useMediaQuery('(max-width: 1023px)');
	const [menuOpen, setMenuOpen] = useState(false);
	const [isMenuOnTop, setIsMenuOnTop] = useState(true);

	useEffect(() => {
		if (menuOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [menuOpen]);

	useEffect(() => {
		const handleScroll = () => {
			setIsMenuOnTop(window.scrollY === 0);
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleMenuItemClick = (ref: React.RefObject<HTMLDivElement>) => {
		if (ref && ref.current) {
			// const offset = isTablet ? 100 : 0;
			const offset = 100;
			const top = ref.current.getBoundingClientRect().top + window.scrollY - offset;
			window.scrollTo({ top, behavior: 'smooth' });
		}

		setMenuOpen(false);
	};

	return (
		<div className={styles['menu-nav']}>
			<div
				className={cn(styles['header-nav-bg'], {
					[styles['not-on-top']]: !isMenuOnTop,
				})}
			>
				<div className={cn(styles['header-nav'], classPageContent)}>
					<div className={styles.logo}>
						<Logotype business small={isMobile} />
					</div>
					{isTablet ? (
						<>
							<button
								type="button"
								className={styles['burger-button']}
								onClick={() => setMenuOpen(true)}
								aria-label="Open menu"
							>
								<Menu size={48} color="white" />
							</button>
							{menuOpen && (
								<ClickAwayListener onClickAway={() => setMenuOpen(false)}>
									<div className={styles['modal-wrapper']}>
										<div className={styles.overlay} onClick={() => setMenuOpen(false)} />
										<div className={styles['mobile-menu']}>
											<button
												type="button"
												className={styles['close-button']}
												onClick={() => setMenuOpen(false)}
												aria-label="Close menu"
											>
												<X size={40} />
											</button>
											<ul className={styles['mobile-menu-list']}>
												{menuItems.map((item) => (
													<li key={item.id}>
														<button type="button" onClick={() => handleMenuItemClick(item.ref)}>
															{item.label}
														</button>
													</li>
												))}
											</ul>
											{!isLoading && (
												<Button
													className="button-book-the-demo"
													elementType="button"
													variant="filled"
													color="secondary"
													size="normal"
													// Disabled due to the Ortto form usage
													// elementType="link"
													// href="https://safe-esteem.typeform.com/to/UVWEowBk"
													// target="_blank"
												>
													Book the demo
												</Button>
											)}
											<SocialMedia showTitle color="#5b6a76" />
										</div>
									</div>
								</ClickAwayListener>
							)}
						</>
					) : (
						<>
							<nav className={styles.menu}>
								<ul>
									{menuItems.map((item) => (
										<li key={item.id}>
											<button
												type="button"
												data-label={item.label}
												onClick={() => handleMenuItemClick(item.ref)}
											>
												{item.label}
											</button>
										</li>
									))}
								</ul>
							</nav>

							<Button
								className={cn(styles['demo-button'], 'button-book-the-demo', {
									[styles.visible]: showDemoButton && !isLoading,
								})}
								elementType="button"
								variant="filled"
								color="secondary"
								size="normal"
								// Disabled due to the Ortto form usage
								// elementType="link"
								// href="https://safe-esteem.typeform.com/to/UVWEowBk"
								// target="_blank"
							>
								Book the demo
							</Button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
